import { supabase } from './client';
import { TeeBox } from '@/types';
import type { GolfCourseApiCourse, TeeMapping } from '@/types/golfCourseApi';

interface ImportCourseInput {
  apiCourse: GolfCourseApiCourse;
  teeMappings: TeeMapping[];
  userId: string;
}

/**
 * Import a course from GolfCourseAPI data into Supabase.
 * Sequential inserts: course → holes (batch) → tee_boxes (per hole per mapped tee) → profile_course_link.
 * Follows the pattern from seeds/courseSeeds.ts.
 */
export async function importCourse({ apiCourse, teeMappings, userId }: ImportCourseInput): Promise<{ courseId?: string; error?: string }> {
  const activeMappings = teeMappings.filter(m => m.dbColor !== null);

  if (activeMappings.length === 0) {
    return { error: 'At least one tee must be mapped' };
  }

  const availableTees = activeMappings.map(m => m.dbColor!) as TeeBox[];

  // Use the first mapped tee as primary source for hole data
  const primaryTee = activeMappings[0];
  const numberOfHoles = primaryTee.numberOfHoles || primaryTee.holes.length || 18;
  const totalPar = primaryTee.parTotal || primaryTee.holes.reduce((sum, h) => sum + h.par, 0);

  // 1. Insert course
  const { data: course, error: courseError } = await supabase
    .from('courses')
    .insert({
      name: apiCourse.course_name || apiCourse.club_name,
      city: apiCourse.location?.city || '',
      state: apiCourse.location?.state || '',
      holes_count: numberOfHoles,
      par: totalPar,
      available_tees: availableTees,
      user_id: userId,
      api_course_id: apiCourse.id,
    })
    .select()
    .single();

  if (courseError) {
    console.error('Error inserting course:', courseError);
    return { error: courseError.message };
  }

  // 2. Insert holes (batch) — API holes are positional (index = hole number)
  const holesData = primaryTee.holes.map((h, idx) => ({
    course_id: course.id,
    hole_number: idx + 1,
    par: h.par,
    handicap: h.handicap || null,
  }));

  const { data: holes, error: holesError } = await supabase
    .from('holes')
    .insert(holesData)
    .select();

  if (holesError) {
    console.error('Error inserting holes:', holesError);
    return { courseId: course.id, error: `Course created but holes failed: ${holesError.message}` };
  }

  // 3. Insert tee_boxes for each hole × each mapped tee
  if (holes && holes.length > 0) {
    const teeBoxRows: { hole_id: string; color: TeeBox; yardage: number | null }[] = [];

    for (const dbHole of holes) {
      for (const mapping of activeMappings) {
        // API holes are positional — match by index (hole_number - 1)
        const apiHole = mapping.holes[dbHole.hole_number - 1];
        teeBoxRows.push({
          hole_id: dbHole.id,
          color: mapping.dbColor!,
          yardage: apiHole?.yardage ?? null,
        });
      }
    }

    const { error: teeError } = await supabase
      .from('tee_boxes')
      .insert(teeBoxRows);

    if (teeError) {
      console.error('Error inserting tee boxes:', teeError);
      // Non-fatal: course and holes exist, tee boxes can be added later
    }
  }

  // 4. Link course to user's profile
  const { error: linkError } = await supabase
    .from('profile_course_link')
    .insert({
      profile_id: userId,
      course_id: course.id,
      is_favorite: true,
    });

  if (linkError) {
    console.error('Error linking course to profile:', linkError);
    // Non-fatal: course exists, user can add it manually
  }

  return { courseId: course.id };
}
